import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeroSection } from '@/components/hero/HeroSection';
import { HeroSectionOposiciones } from '@/components/hero/HeroSectionOposiciones';
import { ChatInputBar } from '@/components/chat/ChatInputBar';
import { UpgradeModal } from '@/components/chat/UpgradeModal';
import { PlanBanner } from '@/components/chat/PlanBanner';
import { TopicGeneratorModal } from '@/components/oposiciones/TopicGeneratorModal';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveMode } from '@/hooks/useEffectiveMode';
import { useOnboarding } from '@/hooks/useOnboarding';
import { createChat, sendMessage, ChatMode } from '@/services/chatService';
import { appModeToChatMode, getChatUrl } from '@/lib/chatModeRoutes';
import { AttachedFile } from '@/hooks/useFileExtraction';
import { useToast } from '@/hooks/use-toast';
import { useMessageLimit } from '@/hooks/useMessageLimit';
import { TOUR_STEPS } from '@/components/onboarding/onboardingSteps';
import { BookOpen, ClipboardCheck, Target, Layers, Youtube, BarChart3, MessageCircle, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { isOposicionesBasicPlan } from '@/lib/oposicionesAccess';


export function ChatHomePage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const { showTour, isExcluded, markTourSeen } = useOnboarding('funcionarios');
  
  const mode = useEffectiveMode();
  const isOposiciones =
    mode.startsWith('oposiciones') ||
    mode === 'base_comun';

  // Map mode → URL prefix for quick actions
  const oposPrefixByMode: Record<string, string> = {
    oposiciones: '/app/oposiciones',
    oposiciones_c1: '/app/oposiciones-c1',
    oposiciones_c2: '/app/oposiciones-c2',
    oposiciones_guardia_civil: '/app/guardia-civil',
    oposiciones_policia_nacional: '/app/policia-nacional',
    oposiciones_iipp: '/app/oposiciones-iipp',
    oposiciones_generico: '/app/base-comun',
    base_comun: '/app/base-comun',
  };

  // Map mode → TopicGeneratorModal variant
  const topicVariant =
    mode === 'oposiciones_c1' ? 'c1'
    : mode === 'oposiciones_iipp' ? 'iipp'
    : mode === 'oposiciones_guardia_civil' ? 'guardia_civil'
    : mode === 'oposiciones_policia_nacional' ? 'policia_nacional'
    : mode === 'oposiciones_c2' ? 'c2'
    : mode === 'oposiciones_generico' || mode === 'base_comun' ? 'generico'
    : 'secretaria';

  // Hover border color per cuerpo
  const hoverBorderClass =
    mode === 'oposiciones_c1' ? 'hover:border-blue-300'
    : mode === 'oposiciones_c2' ? 'hover:border-orange-300'
    : mode === 'oposiciones_guardia_civil' ? 'hover:border-green-300'
    : mode === 'oposiciones_policia_nacional' ? 'hover:border-sky-300'
    : mode === 'oposiciones_iipp' ? 'hover:border-red-300'
    : mode === 'oposiciones_generico' || mode === 'base_comun' ? 'hover:border-amber-300'
    : 'hover:border-violet-300';
  
  const userPlan = profile?.plan || 'free';
  const isBasicOposicionesPlan = isOposicionesBasicPlan(userPlan);
  const { isLimitReached, remaining } = useMessageLimit(user?.id, userPlan, mode === 'funcionarios' ? 'funcionarios' : 'opositores');

  const handleSendMessage = async (content: string, files?: AttachedFile[]) => {
    if (!user) {
      toast({ title: 'Error', description: 'Debes iniciar sesión', variant: 'destructive' });
      return;
    }

    if (isLimitReached) {
      setUpgradeModalOpen(true);
      return;
    }

    try {
      const chatMode: ChatMode = appModeToChatMode(mode);
      const newChat = await createChat(user.id, chatMode);
      const chatPath = getChatUrl(newChat.id, chatMode);

      if (files && files.length > 0) {
        navigate(chatPath, {
          state: {
            pendingMessage: content,
            pendingFiles: files.map(af => af.file),
          },
        });
        return;
      } else {
        await sendMessage(newChat.id, content, 'user');
      }

      navigate(chatPath);
    } catch (error) {
      console.error('Error creating chat:', error);
      toast({ title: 'Error', description: 'No se pudo crear la consulta', variant: 'destructive' });
    }
  };

  const openLegalSearch = async () => {
    if (!user) {
      toast({ title: 'Error', description: 'Debes iniciar sesión', variant: 'destructive' });
      return;
    }

    try {
      const chatMode: ChatMode = appModeToChatMode(mode);
      const newChat = await createChat(user.id, chatMode);
      navigate(getChatUrl(newChat.id, chatMode), { state: { openLegalSearch: true } });
    } catch (error) {
      console.error('Error creating legal search chat:', error);
      toast({ title: 'Error', description: 'No se pudo abrir el buscador', variant: 'destructive' });
    }
  };

    const oposPrefix = oposPrefixByMode[mode] || '/app/oposiciones';
    const caseLabel = mode === 'oposiciones_c1' ? 'Supuesto' : 'Caso Práctico';

    const quickActions = isOposiciones ? [
      { label: 'Test', icon: '📝', route: `${oposPrefix}/test` },
      ...(!isBasicOposicionesPlan && mode !== 'oposiciones_policia_nacional' ? [{ label: caseLabel, icon: '🎯', route: `${oposPrefix}/caso` }] : []),
      { label: 'Flashcards', icon: '🗂️', route: `${oposPrefix}/flashcards` },
      ...(!isBasicOposicionesPlan ? [
        { label: 'Recursos', icon: '📚', route: `${oposPrefix}/recursos` },
        { label: 'Generar Material', icon: '✨', onClick: () => setTopicModalOpen(true) },
      ] : []),
      { label: 'Mi Progreso', icon: '📊', route: `${oposPrefix}/progreso` },
    ] : [];

    const heroVariant = mode === 'oposiciones_c1' ? 'oposiciones_c1'
      : mode === 'oposiciones_c2' ? 'oposiciones_c2'
      : mode === 'oposiciones_guardia_civil' ? 'oposiciones_guardia_civil'
      : mode === 'oposiciones_policia_nacional' ? 'oposiciones_policia_nacional'
      : mode === 'oposiciones_iipp' ? 'oposiciones_iipp'
      : 'secretaria';

    return (
      <>
        <div className="flex flex-col h-full bg-white">
          <div className="flex-1 overflow-y-auto">
            {isOposiciones ? (
              <HeroSectionOposiciones variant={heroVariant} />
            ) : (
              <HeroSection />
            )}

            {/* Quick actions for oposiciones */}
            {isOposiciones && (
              <div className="max-w-2xl mx-auto px-4 pb-4">
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {quickActions.map((action) => (
                    <Card
                      key={action.label}
                      onClick={() => action.onClick ? action.onClick() : navigate(action.route!)}
                      className={`p-3 cursor-pointer hover:shadow-md ${hoverBorderClass} transition-all text-center`}
                    >
                      <span className="text-xl">{action.icon}</span>
                      <p className="text-[11px] font-medium mt-1 leading-tight">{action.label}</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="px-4 pb-2 space-y-2">
            <PlanBanner plan={userPlan} mode={mode === 'funcionarios' ? 'funcionarios' : 'opositores'} />
          </div>

          {!isOposiciones && (
            <div className="px-4 pb-3">
              <div className="max-w-2xl mx-auto">
                <button
                  onClick={openLegalSearch}
                  className="w-full rounded-2xl border border-[#002B5B]/15 bg-gradient-to-r from-[#002B5B] to-[#06D3AA] px-4 py-3 text-left text-white shadow-sm transition-transform hover:scale-[1.01] hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
                      <Search className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-tight">Buscar jurisprudencia, DGT o TEAC</p>
                      <p className="text-xs text-white/85">
                        Activa la búsqueda en vivo y guarda resultados para consultas futuras.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
          
          <div data-tour="chat-input">
            <ChatInputBar 
              onSendMessage={handleSendMessage} 
              onLegalSearch={openLegalSearch}
              disabled={false}
              isLimitReached={isLimitReached}
              remaining={remaining}
              onUpgradeClick={() => setUpgradeModalOpen(true)}
              variant={isOposiciones ? 'oposiciones' : 'funcionarios'}
            />
          </div>
        </div>

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
      />

      <TopicGeneratorModal
        open={topicModalOpen}
        onOpenChange={setTopicModalOpen}
        variant={topicVariant}
      />

      {/* Onboarding Tour for Funcionarios */}
      {showTour && !isExcluded && !isOposiciones && (
        <OnboardingTour
          steps={TOUR_STEPS.funcionarios}
          onComplete={markTourSeen}
        />
      )}
    </>
  );
}
